import React, { useMemo } from 'react';
import { useLocale, WithLocaleProps } from 'locale';
import {
  getBuildingClassKey,
  BuildingClass,
} from 'utils/entities';
import {
  Body,
  PropertyDetailsText,
  NewPropertyDetailsSeparator,
  PropertyDetailsSeparator,
} from 'ds/components/ListingCard';
import {
  FirstLineLabel,
  LineLabels,
  SecondLineLabel,
  ThirdLineLabel,
  FirstLineWrapper,
  MarketplaceTypeLabel,
} from '../styled';
import { AgentTags } from '../AgentTags';
import { PropertyDetailsWrapper } from 'ds/components/ListingCard';
import { BreakpointValue } from 'consts/breakpoints';
import NotificationTag from '../NotificationTag';
import renderLineItem from 'components/labelLineItem';
import { UniversalCardLineLabels, useUniversalCardAddress } from '../utils';
import { BulletinUniversalCardProps, ExtraProps } from 'components/cards/types';


export interface UniversalBulletinCardProps extends BulletinUniversalCardProps, ExtraProps {
  breakpoint: BreakpointValue;
  carousel: JSX.Element;
  handleOnMouseEnter: () => void;
  handleOnMouseLeave: () => void;
}

const getInfoLineConfig = (bedsCount: number, size: number, floor: number, buildingClass: BuildingClass, { t, formatArea }: WithLocaleProps, breakpoint: BreakpointValue) => {
  const isMobile = breakpoint <= 2;
  const separator = isMobile ? <NewPropertyDetailsSeparator /> : <PropertyDetailsSeparator isPromoted={undefined} />;
  return [
    {
      label: UniversalCardLineLabels.BuildingClass,
      separator,
      showCondition: Boolean(buildingClass),
      render: () => (
        <PropertyDetailsText data-auto="property-class">
          {t('unitPage.buildingClass', { buildingClass: getBuildingClassKey(buildingClass), isCommercial: false })}
        </PropertyDetailsText>
      ),
    },
    {
      label: UniversalCardLineLabels.Beds,
      separator,
      showCondition: typeof bedsCount === 'number',
      render: () => (
        <PropertyDetailsText
          isPromoted={false}
          data-auto="property-rooms"
        >
          {t('listingCard.bedsCountLabel', { bedsCount })}
        </PropertyDetailsText>
      ),
    },
    {
      label: UniversalCardLineLabels.Floor,
      separator,
      showCondition: Boolean(floor),
      render: () => (
        <PropertyDetailsText data-auto="property-floor">
          {t('listingCard.floorLabel', { floor })}
        </PropertyDetailsText>
      ),
    },
    {
      label: UniversalCardLineLabels.Size,
      separator,
      showCondition: Boolean(size) && size > 0,
      render: () => (
        <PropertyDetailsText data-auto="property-size">
          {formatArea(size)}
        </PropertyDetailsText>
      ),
    },
  ];
};

const UniversalBulletinCard: React.FC<UniversalBulletinCardProps> = (props) => {

  const localeProps = useLocale();
  const { formatMoney, t } = localeProps;
  const {
    withPaddingBottom,
    city,
    notificationTags,
    carousel,
    breakpoint,
    handleOnMouseEnter,
    handleOnMouseLeave,
    buildingClass,
    bedsCount,
    floor,
    size,
    isSearchInWholeCountry,
    resolutionPreferences,
    neighbourhood,
    streetNumber,
    streetName,
    unitNumber,
    showMarketplaceType,
  } = props;

  const [ notificationTag ] = notificationTags || [];
  const isOnSeparateLine = Boolean(props.isExclusive && props.madadSearchResult);

  const infoLineConfig = useMemo(() => {
    return getInfoLineConfig(bedsCount, size, floor, buildingClass, localeProps, breakpoint).filter(({ showCondition }) => showCondition);
  }, [ bedsCount, size, buildingClass, localeProps, breakpoint, getInfoLineConfig ]);

  const primaryAddress = useUniversalCardAddress(
    streetName,
    streetNumber,
    neighbourhood,
    unitNumber,
    city,
    resolutionPreferences,
    isSearchInWholeCountry
  );

  return (
    <div onMouseEnter={handleOnMouseEnter} onMouseLeave={handleOnMouseLeave}>
      {carousel}
      <Body withPaddingBottom={withPaddingBottom} className="universal-card-body-wrapper">
        {showMarketplaceType ? (
          <MarketplaceTypeLabel>
            {t('listingCard.marketplaceTypeLabel', { isCommercial: false })}
          </MarketplaceTypeLabel>
        ) : null}
        <LineLabels data-auto="property-details">
          <FirstLineLabel isHighlighted={false} weight="bold">
            <FirstLineWrapper
              data-auto="property-price"
              isFlexColumn={isOnSeparateLine}
            >
              {formatMoney(props.price)}
              {props.isAgent ? (
                <AgentTags
                  isExclusive={props.isExclusive}
                  madadSearchResult={props.madadSearchResult}
                  isOnSeparateLine={isOnSeparateLine}
                />
              ) : null}
            </FirstLineWrapper>
          </FirstLineLabel>
          <SecondLineLabel>
            {infoLineConfig.map(renderLineItem)}
          </SecondLineLabel>
          <ThirdLineLabel>
            <PropertyDetailsWrapper data-auto="property-address">
              <PropertyDetailsText>{primaryAddress}</PropertyDetailsText>
            </PropertyDetailsWrapper>
          </ThirdLineLabel>
          {notificationTag && city ? <PropertyDetailsText faded>{city}</PropertyDetailsText> : null}
        </LineLabels>
        {notificationTag ? (
          <NotificationTag tag={notificationTag} />
        ) : null}
      </Body>
    </div>
  );
};

export default UniversalBulletinCard;
